import { useState, useEffect } from 'react';
import { createClient } from '@blinkdotnew/sdk';
import { Label } from '@/components/ui/label';
import { Slider } from '@/components/ui/slider';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { toast } from '@/hooks/use-toast';
import { RefreshCw, Save, Settings2 } from 'lucide-react';

interface SyncSettings {
  maxRetries: number;
  initialDelay: number;
  maxDelay: number;
  backoffMultiplier: number;
}

interface SyncSettingsFormProps {
  userId?: string;
}

const blink = createClient({ 
  projectId: 'lemon-ui-agentic-email-ai-agent-ben3ou87', 
  authRequired: false 
});

export default function SyncSettingsForm({ userId }: SyncSettingsFormProps) {
  const [settings, setSettings] = useState<SyncSettings>({
    maxRetries: 3,
    initialDelay: 2000,
    maxDelay: 10000,
    backoffMultiplier: 2,
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Load settings from database
  useEffect(() => {
    if (!userId) return;

    const loadSettings = async () => {
      try {
        const dbSettings = await blink.db.userSyncSettings.list({
          where: { userId },
          limit: 1,
        });

        if (dbSettings && dbSettings.length > 0) {
          const s = dbSettings[0];
          setSettings({
            maxRetries: s.maxRetries || 3,
            initialDelay: s.initialDelay || 2000,
            maxDelay: s.maxDelay || 10000,
            backoffMultiplier: s.backoffMultiplier || 2,
          });
        } else {
          // Migrate from localStorage if exists
          const localMaxRetries = localStorage.getItem('sync_max_retries');
          const localInitialDelay = localStorage.getItem('sync_initial_delay');
          const localMaxDelay = localStorage.getItem('sync_max_delay');
          const localBackoffMultiplier = localStorage.getItem('sync_backoff_multiplier');

          if (localMaxRetries || localInitialDelay || localMaxDelay || localBackoffMultiplier) {
            const migratedSettings = {
              maxRetries: Number(localMaxRetries) || 3,
              initialDelay: Number(localInitialDelay) || 2000,
              maxDelay: Number(localMaxDelay) || 10000,
              backoffMultiplier: Number(localBackoffMultiplier) || 2,
            };

            setSettings(migratedSettings);

            // Save migrated settings to DB
            await blink.db.userSyncSettings.create({
              id: `sync_${userId}_${Date.now()}`,
              userId,
              ...migratedSettings,
            });

            // Clear localStorage
            localStorage.removeItem('sync_max_retries');
            localStorage.removeItem('sync_initial_delay');
            localStorage.removeItem('sync_max_delay');
            localStorage.removeItem('sync_backoff_multiplier');

            toast({
              title: 'Settings migrated',
              description: 'Your sync settings have been moved to the database',
            });
          }
        }
      } catch (error) {
        console.error('Error loading sync settings:', error);
        toast({
          title: 'Error',
          description: 'Failed to load sync settings',
          variant: 'destructive',
        });
      } finally {
        setLoading(false);
      }
    };

    loadSettings();
  }, [userId]);

  const handleSave = async () => {
    if (!userId) return;

    setSaving(true);
    try {
      const existing = await blink.db.userSyncSettings.list({
        where: { userId },
        limit: 1,
      });

      if (existing && existing.length > 0) {
        await blink.db.userSyncSettings.update(existing[0].id, {
          ...settings,
          updatedAt: new Date().toISOString(),
        });
      } else {
        await blink.db.userSyncSettings.create({
          id: `sync_${userId}_${Date.now()}`,
          userId,
          ...settings,
        });
      }

      toast({
        title: 'Settings saved',
        description: 'Sync retry configuration saved successfully',
      });
    } catch (error) {
      console.error('Error saving sync settings:', error);
      toast({
        title: 'Error',
        description: 'Failed to save sync settings',
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    const defaults: SyncSettings = {
      maxRetries: 3,
      initialDelay: 2000,
      maxDelay: 10000,
      backoffMultiplier: 2,
    };

    setSettings(defaults);

    if (userId) {
      try {
        const existing = await blink.db.userSyncSettings.list({
          where: { userId },
          limit: 1,
        });

        if (existing && existing.length > 0) {
          await blink.db.userSyncSettings.update(existing[0].id, {
            ...defaults,
            updatedAt: new Date().toISOString(),
          });
        }

        toast({
          title: 'Settings reset',
          description: 'Sync retry settings restored to defaults',
        });
      } catch (error) {
        console.error('Error resetting sync settings:', error);
      }
    }
  };

  if (loading) {
    return <div className="text-sm text-muted-foreground">Loading settings...</div>;
  }

  return (
    <div className="space-y-4">
      <div>
        <div className="flex items-center justify-between mb-2">
          <Label htmlFor="max-retries">Maximum Retry Attempts</Label>
          <span className="text-sm text-muted-foreground">{settings.maxRetries} attempts</span>
        </div>
        <Slider
          id="max-retries"
          value={[settings.maxRetries]}
          onValueChange={([value]) => setSettings({ ...settings, maxRetries: value })}
          min={1}
          max={10}
          step={1}
          className="w-full"
        />
        <p className="text-xs text-muted-foreground mt-2">
          Number of times to retry a failed sync operation before giving up
        </p>
      </div>

      <div>
        <div className="flex items-center justify-between mb-2">
          <Label htmlFor="initial-delay">Initial Backoff Delay</Label>
          <span className="text-sm text-muted-foreground">{settings.initialDelay / 1000}s</span>
        </div>
        <Slider
          id="initial-delay"
          value={[settings.initialDelay]}
          onValueChange={([value]) => setSettings({ ...settings, initialDelay: value })}
          min={500}
          max={10000}
          step={500}
          className="w-full"
        />
        <p className="text-xs text-muted-foreground mt-2">
          Initial wait time before first retry attempt (in milliseconds)
        </p>
      </div>

      <div>
        <div className="flex items-center justify-between mb-2">
          <Label htmlFor="max-delay">Maximum Backoff Delay</Label>
          <span className="text-sm text-muted-foreground">{settings.maxDelay / 1000}s</span>
        </div>
        <Slider
          id="max-delay"
          value={[settings.maxDelay]}
          onValueChange={([value]) => setSettings({ ...settings, maxDelay: value })}
          min={1000}
          max={60000}
          step={1000}
          className="w-full"
        />
        <p className="text-xs text-muted-foreground mt-2">
          Maximum wait time between retry attempts (in milliseconds)
        </p>
      </div>

      <div>
        <div className="flex items-center justify-between mb-2">
          <Label htmlFor="backoff-multiplier">Backoff Multiplier</Label>
          <span className="text-sm text-muted-foreground">{settings.backoffMultiplier}x</span>
        </div>
        <Slider
          id="backoff-multiplier"
          value={[settings.backoffMultiplier]}
          onValueChange={([value]) => setSettings({ ...settings, backoffMultiplier: value })}
          min={1}
          max={5}
          step={0.5}
          className="w-full"
        />
        <p className="text-xs text-muted-foreground mt-2">
          How much to increase delay between each retry (exponential backoff)
        </p>
      </div>

      <Alert>
        <Settings2 className="h-4 w-4" />
        <AlertDescription className="text-xs">
          <strong>Advanced Settings:</strong> These control how aggressively the system retries failed sync operations. 
          Higher values = more patient retries. Lower values = faster failure detection.
        </AlertDescription>
      </Alert>

      <div className="flex gap-2 pt-4 border-t">
        <Button variant="outline" onClick={handleReset} disabled={saving}>
          <RefreshCw className="mr-2 h-4 w-4" />
          Reset to Defaults
        </Button>
        <Button onClick={handleSave} disabled={saving} className="flex-1 bg-primary hover:bg-primary/90">
          <Save className="mr-2 h-4 w-4" />
          {saving ? 'Saving...' : 'Save Configuration'}
        </Button>
      </div>
    </div>
  );
}
